import hubspotIcon from "@/assets/integrations/hubspot.svg";
import { ButtonLoading } from "@/components/button-loading";
import { HubspotReconnectAlert } from "@/components/hubspot-reconnect-alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { HubSpotListDTO } from "@shaarp/common";
import { AxiosError } from "axios";
import {
  CheckIcon,
  ChevronsUpDownIcon,
  Loader2Icon,
  PlusIcon,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useToastError } from "./ui/use-toast";

type HubspotListType = "company" | "contact";

type HubspotListDialogProps = {
  type: HubspotListType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  count: number;
  isSaving: boolean;
  onSave: (list: HubSpotListDTO) => void;
  translationPrefix: string;
};

/**
 * Shared dialog for selecting or creating a HubSpot list (company or contact).
 * Handles list fetching, creation, reconnect flow, and list selection.
 */
export function HubspotListDialog({
  type,
  open,
  onOpenChange,
  count,
  isSaving,
  onSave,
  translationPrefix,
}: HubspotListDialogProps) {
  const toastError = useToastError();
  const api = useApi();
  const { t } = useTranslation();

  const listEndpoint =
    type === "company"
      ? "/integration/crm/hubspot/company-lists"
      : "/integration/crm/hubspot/contact-lists";

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [lists, setLists] = useState<HubSpotListDTO[]>([]);
  const [isLoadingLists, setIsLoadingLists] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedList, setSelectedList] = useState<HubSpotListDTO | null>(null);
  const [newListName, setNewListName] = useState("");
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [needsReconnect, setNeedsReconnect] = useState(false);

  const isPermissionError = (error: unknown): boolean => {
    if (error instanceof AxiosError) {
      const status = error.response?.status;
      return status === 403 || status === 401;
    }
    return false;
  };

  const fetchLists = useCallback(async () => {
    setIsLoadingLists(true);
    setNeedsReconnect(false);
    try {
      const { data } = await api.get<HubSpotListDTO[]>(listEndpoint);
      setLists(data);
    } catch (error) {
      if (isPermissionError(error)) {
        setNeedsReconnect(true);
      } else {
        toastError();
      }
    } finally {
      setIsLoadingLists(false);
    }
  }, [api, toastError, listEndpoint]);

  useEffect(() => {
    if (open) {
      fetchLists();
      setSelectedList(null);
      setNewListName("");
      setIsCreateMode(false);
      setNeedsReconnect(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const createList = async () => {
    if (!newListName.trim()) return;

    setIsCreating(true);
    try {
      const { data: newList } = await api.post<HubSpotListDTO>(listEndpoint, {
        name: newListName.trim(),
      });
      setLists((prev) => [newList, ...prev]);
      setSelectedList(newList);
      setNewListName("");
      setIsCreateMode(false);
    } catch {
      toastError();
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[480px] w-full">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <img src={hubspotIcon} alt="HubSpot" className="w-5 h-5" />
            {t(`${translationPrefix}.dialogTitle`)}
          </DialogTitle>
          <DialogDescription>
            {t(`${translationPrefix}.dialogDescription`, { count })}
          </DialogDescription>
        </DialogHeader>
        <Separator />
        {needsReconnect ? (
          <HubspotReconnectAlert
            translationPrefix={translationPrefix}
            onReconnected={fetchLists}
          />
        ) : (
          <div className="space-y-4">
            <div className="grid w-full items-center gap-1.5">
              <Label>
                {isCreateMode
                  ? t(`${translationPrefix}.newList`)
                  : t(`${translationPrefix}.hubspotLists`)}
              </Label>
              {isCreateMode ? (
                <div className="flex items-center gap-2">
                  <Input
                    placeholder={t(`${translationPrefix}.newListPlaceholder`)}
                    value={newListName}
                    onChange={(e) => setNewListName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        createList();
                      }
                      if (e.key === "Escape") {
                        setIsCreateMode(false);
                        setNewListName("");
                      }
                    }}
                    autoFocus
                    disabled={isCreating}
                  />
                  <ButtonLoading
                    isLoading={isCreating}
                    size="sm"
                    className="h-9 shrink-0"
                    onClick={createList}
                    disabled={!newListName.trim() || isCreating}
                  >
                    {t(`${translationPrefix}.create`)}
                  </ButtonLoading>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9 shrink-0"
                    onClick={() => {
                      setIsCreateMode(false);
                      setNewListName("");
                    }}
                    disabled={isCreating}
                  >
                    {t("common.cancel")}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Popover
                    open={popoverOpen}
                    onOpenChange={setPopoverOpen}
                    modal
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={popoverOpen}
                        className="flex-1 min-w-0 justify-between font-normal"
                        disabled={isLoadingLists}
                      >
                        {isLoadingLists ? (
                          <span className="flex items-center gap-2 text-muted-foreground">
                            <Loader2Icon className="w-3.5 h-3.5 animate-spin" />
                            {t(`${translationPrefix}.selectList`)}
                          </span>
                        ) : selectedList ? (
                          <span className="flex items-center gap-2 truncate">
                            {selectedList.name}
                            <Badge
                              variant={
                                selectedList.size === 0
                                  ? "outline"
                                  : "secondary"
                              }
                              className="ml-auto shrink-0"
                            >
                              {selectedList.size}
                            </Badge>
                          </span>
                        ) : (
                          <span className="text-muted-foreground">
                            {t(`${translationPrefix}.selectList`)}
                          </span>
                        )}
                        <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="p-0"
                      style={{
                        width: "var(--radix-popover-trigger-width)",
                      }}
                      align="start"
                    >
                      <Command>
                        <CommandInput
                          placeholder={t(`${translationPrefix}.search`)}
                        />
                        <CommandList>
                          <CommandEmpty>
                            {t(`${translationPrefix}.noListFound`)}
                          </CommandEmpty>
                          {lists.length > 0 && (
                            <CommandGroup>
                              {lists.map((list) => (
                                <CommandItem
                                  key={list.listId}
                                  value={list.name}
                                  onSelect={() => {
                                    setSelectedList(list);
                                    setPopoverOpen(false);
                                  }}
                                >
                                  <CheckIcon
                                    className={cn(
                                      "mr-2 h-4 w-4 shrink-0",
                                      selectedList?.listId === list.listId
                                        ? "opacity-100"
                                        : "opacity-0",
                                    )}
                                  />
                                  <span className="truncate">{list.name}</span>
                                  <Badge
                                    variant={
                                      list.size === 0 ? "outline" : "secondary"
                                    }
                                    className="ml-auto shrink-0"
                                  >
                                    {list.size}
                                  </Badge>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          )}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-9 w-9 shrink-0"
                          onClick={() => setIsCreateMode(true)}
                          disabled={isLoadingLists}
                        >
                          <PlusIcon className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {t(`${translationPrefix}.newList`)}
                      </TooltipContent>
                    </Tooltip>
                  </Popover>
                </div>
              )}
            </div>
          </div>
        )}
        <Separator />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">{t("common.cancel")}</Button>
          </DialogClose>
          {!needsReconnect && (
            <ButtonLoading
              isLoading={isSaving}
              disabled={!selectedList || isSaving}
              onClick={() => selectedList && onSave(selectedList)}
            >
              {t(`${translationPrefix}.saveToHubspot`)}
            </ButtonLoading>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
